import { useCallback, useEffect } from 'react';
import { ui } from '@procuros/datachecks';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { FieldState } from 'components/Form/types';
import { Translate } from 'components/Translate/Translate';
import { useEnumerableOptions } from 'components/WebEDI/helpers';

import { CheckboxGroup } from '../CheckboxGroup/CheckboxGroup';

type SchemableFormCheckboxGroupProps = {
  field: ui.EnumerableField;
  value: Array<string>;
  onChange: (value: Array<string>) => void;
  onBlur: () => void;
  error: WebEDIError | undefined;
  totalNumberOfErrors: number;
  onShowFieldErrorModal?: (showFieldErrorModal: boolean) => void;
  required: boolean;
  disabled: boolean;
  fieldState: FieldState;
  variant?: 'large';
  name: string;
};

export const SchemableFormCheckboxGroup = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  totalNumberOfErrors,
  onShowFieldErrorModal,
  required,
  disabled,
  fieldState,
  variant,
  name,
}: SchemableFormCheckboxGroupProps) => {
  const { options, hasResolved } = useEnumerableOptions(field.type_config);
  const isValidating = useWebEDIStore((state) => state.isValidating);
  const { ErrorModalComponent, useErrorModal } = useErrorContext();
  const { showErrorModal, setShowErrorModal, floatingStyles, refs } = useErrorModal();
  const isLargeVariant = variant === 'large';

  const onCheckboxFocus = useCallback(() => {
    setShowErrorModal(true);
  }, [setShowErrorModal]);

  const onCheckboxBlur = useCallback(() => {
    onBlur();
    setShowErrorModal(false);
  }, [onBlur, setShowErrorModal]);

  const handleToggle = useCallback(
    (optionValue: string) => {
      const currentValues = value ?? [];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter((v) => v !== optionValue)
        : [...currentValues, optionValue];
      onChange(newValues);
    },
    [value, onChange],
  );

  useEffect(() => {
    if (error && showErrorModal) {
      onShowFieldErrorModal?.(true);
    }
  }, [error, showErrorModal, onShowFieldErrorModal]);

  useEffect(() => {
    if (!hasResolved) {
      return;
    }

    const validValues = new Set(options.map((option) => option.value));
    const currentValues = value ?? [];
    const filteredValues = currentValues.filter((v) => validValues.has(v));

    if (filteredValues.length !== currentValues.length) {
      onChange(filteredValues);
    }
  }, [options, hasResolved, value, onChange]);

  const inlineErrors =
    isLargeVariant && error?.description ? { message: error.description, type: error.type ?? 'error' } : undefined;

  return (
    <div ref={isLargeVariant ? undefined : refs.setReference}>
      <CheckboxGroup.Group
        label={field.label}
        legendVariant={isLargeVariant ? 'large' : 'default'}
        required={required}
        description={field.description ? <Translate i18nKey={field.description} /> : undefined}
      >
        {options.map((option) => (
          <CheckboxGroup.Item
            key={option.value}
            name={name}
            value={option.value}
            label={option.label}
            checked={(value ?? []).includes(option.value)}
            onChange={() => handleToggle(option.value)}
            onFocus={isLargeVariant ? undefined : onCheckboxFocus}
            onBlur={isLargeVariant ? undefined : onCheckboxBlur}
            disabled={disabled}
            hasErrors={fieldState === 'error'}
          />
        ))}
      </CheckboxGroup.Group>

      {inlineErrors && <p className="mt-1 text-sm text-red-700">{inlineErrors.message}</p>}

      {!isLargeVariant && showErrorModal && ErrorModalComponent && (
        <ErrorModalComponent
          viewMode={false}
          error={error}
          ref={refs.setFloating}
          style={floatingStyles}
          numberOfErrors={totalNumberOfErrors}
          isValidating={isValidating}
          isPendingValidation={fieldState === 'pending-validation'}
          fieldDescription={field.description}
        />
      )}
    </div>
  );
};

SchemableFormCheckboxGroup.displayName = 'SchemableFormCheckboxGroup';
